import { useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";
import type { Exercise } from "@heraion/shared";

import { busyAria } from "../a11y";
import { Icon } from "../Icon/Icon";
import { useInboxContext } from "../Inbox/InboxProvider";
import { Badge } from "../primitives/Badge";
import { PageSheet } from "../primitives/PageSheet";
import { useLocale } from "../primitives/useLocale";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { DurableWriteFn, NotifyFn } from "../seams";
import type { UiTheme } from "../theme";
import { useBreakpoint } from "../useBreakpoint";
import { LibraryResults } from "../Library/LibraryResults";
import { libraryItemName, useLibrary, type LibraryItem } from "../Library/useLibrary";
import { useLibraryBrowse } from "../Library/useLibraryBrowse";

// CO-23 — the picker in front of the assignment step that already exists
// (PATCH /:id/assignees → proposeAssignments, unchanged): opened from the
// coach's AthleteDetail, it lets the coach assign one of THEIR OWN
// programs/workouts to the athlete already on screen. Same chrome primitive
// as the standalone AssignSheet (PageSheet), but it isn't a clone of it —
// AssignSheet edits the roster of an ALREADY-CHOSEN item; this picks WHICH
// item, reusing the Library's own browse/render split (useLibraryBrowse +
// LibraryResults, see LibraryBrowser.tsx's header) locked to the "personal"
// tab with a selectable row as its `renderItem`.
//
// 🔴 Correctness, not style: only `source === "personal"` rows are offered
// (useLibraryBrowse's `lockedFilter`) — PATCH /:id/assignees writes the
// CALLER's own document, so an item assigned TO the coach by someone else
// isn't the coach's to re-assign (next/coaching.md:435 — the library test is
// who created it, never what hat they wore).
//
// The write MERGES into `assignedTo` (never replaces it — a program shared
// with three athletes must not lose two), and an athlete already on the list
// is a no-op with a message via the injected `notify` seam (Alert on mobile,
// window.confirm's cousin `window.alert` on web — the same seam
// AssignSheet's error banner does NOT need, because this is a one-way
// message, not a choice).
export type ProgramPickerSheetProps = {
  theme: UiTheme;
  visible: boolean;
  apiFetch: ProgramApiFetch;
  /** Host write seam — mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
  /** Live catalog for the picker's own tag-filter axes (muscle/equipment) —
   *  the row itself needs no catalog, only useLibraryBrowse's filter options
   *  do (same contract as LibraryBrowser's `catalog`). */
  catalog: Exercise[] | null;
  /** The signed-in coach's own sub — same "not yet known" contract as
   *  useLibrary's own callerSub (null ⇒ no personal rows resolve yet). */
  callerSub: string | null;
  /** The athlete this picker assigns TO — fixed by the host (it is already
   *  inside that athlete's own detail screen), never a roster to choose
   *  from, unlike AssignSheet's multi-athlete band. */
  athleteSub: string;
  /** The athlete's display name, for the sheet's title. */
  athleteName: string;
  /** Platform seam for the "already assigned" one-way message — injected by
   *  the mount (Alert mobile / window.alert web), never duplicated here. */
  notify: NotifyFn;
  onCancel: () => void;
  /** Safe-area insets (px) — 0 on web, same contract as AssignSheet/PageSheet. */
  topInset?: number;
  bottomInset?: number;
};

const assignedToOf = (item: Extract<LibraryItem, { source: "personal" }>): string[] =>
  item.kind === "program" ? item.program.assignedTo : item.plan.assignedTo;

export function ProgramPickerSheet({
  theme,
  visible,
  apiFetch,
  durableWrite,
  catalog,
  callerSub,
  athleteSub,
  athleteName,
  notify,
  onCancel,
  topInset = 0,
  bottomInset = 0,
}: ProgramPickerSheetProps) {
  const { t } = useTranslation();
  const lang = useLocale();
  const { wide } = useBreakpoint();
  const { refresh: refreshInbox } = useInboxContext();
  const styles = makeStyles(theme);

  // Own data layer, like LibraryBrowser — no lib/browse state is threaded in
  // from the host, so a picker opened on one athlete never shares state with
  // one opened on another. `authoredAs: "coach"` — this only ever mounts from
  // the coach's own athlete drill-down.
  const lib = useLibrary({ apiFetch, durableWrite, callerSub, authoredAs: "coach" });
  const browse = useLibraryBrowse({ lib, callerSub, catalog, lockedFilter: "personal" });

  const alreadyLabel = t("coach.assignPicker.alreadyTitle", {
    defaultValue: "Already assigned",
  });
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSelect = async (item: LibraryItem) => {
    if (item.source !== "personal" || busyKey) return;
    const current = assignedToOf(item);
    if (current.includes(athleteSub)) {
      // No-op, not an error — re-picking an already-assigned item isn't a
      // mistake, just nothing left to do.
      notify(
        alreadyLabel,
        t("coach.assignPicker.alreadyBody", {
          name: athleteName,
          defaultValue: "{{name}} already has this.",
        }),
      );
      return;
    }
    setBusyKey(item.key);
    setError(null);
    // The "" sentinel a never-shared row can carry is filtered here too —
    // same convention as useLibraryAssignSheet's own draft merge.
    const assignedTo = [...current.filter((s) => s !== ""), athleteSub];
    const err =
      item.kind === "program"
        ? await lib.setProgramAssignees(item.program, { assignedTo })
        : await lib.setPlanAssignees(item.plan, { assignedTo });
    setBusyKey(null);
    if (err) {
      console.warn("[coach] assign_failed", err.message);
      setError(
        t("coach.assignPicker.errAssign", {
          defaultValue: "Could not assign. Try again.",
        }),
      );
      return;
    }
    // CO-22: this PROPOSES, it does not assign (a `pending` row the athlete
    // can refuse, next/coaching.md §4), so the copy says "Sent". When the
    // athlete is the coach themself (alter ego) the proposal lands in their
    // own inbox: no popup, the bell row is the confirmation, so refresh the
    // inbox now (the bell otherwise refetches only when opened).
    if (athleteSub === callerSub) {
      refreshInbox().catch(() => {});
    } else {
      notify(
        t("coach.assignPicker.assignedTitle", { defaultValue: "Sent" }),
        t("coach.assignPicker.assignedBody", {
          name: athleteName,
          defaultValue: "Sent to {{name}} — waiting for them to accept.",
        }),
      );
    }
    onCancel();
  };

  const renderItem = (item: LibraryItem) => {
    if (item.source !== "personal") return null;
    const name = libraryItemName(item, lang);
    const meta =
      item.kind === "program"
        ? t("templates.programMeta", {
            weeks: item.program.cycleWeeks,
            sessions: item.program.sessionsPerWeek,
            defaultValue: "{{weeks}}-week cycle · {{sessions}}× / week",
          })
        : t("templates.planMeta", {
            n: item.plan.entries.length,
            defaultValue: "{{n}} exercise(s)",
          });
    const busy = busyKey === item.key;
    const disabled = busyKey !== null;
    const assigned = assignedToOf(item).includes(athleteSub);
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          assigned
            ? `${name}, ${alreadyLabel}`
            : t("coach.assignPicker.rowA11y", {
                name,
                defaultValue: "Assign {{name}}",
              })
        }
        onPress={() => void onSelect(item)}
        disabled={disabled}
        hitSlop={8}
        {...busyAria(busy)}
        style={({ pressed }) => [
          styles.row,
          pressed && !disabled && styles.pressed,
          disabled && !busy && styles.rowDisabled,
        ]}
      >
        <View style={styles.rowText}>
          <Text style={styles.rowName} numberOfLines={2}>
            {name}
          </Text>
          <Text style={styles.rowMeta}>{meta}</Text>
        </View>
        {busy ? (
          <ActivityIndicator color={theme.palette.ink2} />
        ) : assigned ? (
          <Badge theme={theme} tone="ok" icon="check" label={alreadyLabel} />
        ) : (
          // 🔴 Visible write affordance (jl-ux-a11y-auditor finding #1) — the
          // whole row IS the assign gesture (writes the athlete's data), and
          // until now the only sign of that was the a11y label, invisible to
          // a sighted coach. Decorative only (pointerEvents="none"): the
          // Pressable above already carries the spoken label, so this must
          // not create a second announced/tappable target on the same row.
          <View style={styles.assignCta} pointerEvents="none">
            <Icon name="users" size={16} color={theme.palette.brand500} />
            <Text style={styles.assignCtaText}>
              {t(item.kind === "plan" ? "plans.assign" : "program.assign", {
                defaultValue: "Assign",
              })}
            </Text>
          </View>
        )}
      </Pressable>
    );
  };

  return (
    <PageSheet
      theme={theme}
      visible={visible}
      onRequestClose={onCancel}
      title={t("coach.assignPicker.title", {
        name: athleteName,
        defaultValue: "Assign to {{name}}",
      })}
      cancelLabel={t("coach.assignPicker.cancel", { defaultValue: "Cancel" })}
      onCancel={onCancel}
      topInset={topInset}
      bottomInset={bottomInset}
    >
      <ScrollView contentContainerStyle={styles.content}>
        <LibraryResults
          theme={theme}
          wide={wide}
          authoredAs={lib.authoredAs}
          browse={browse}
          banners={{
            cloneFlash: lib.cloneFlash,
            cloneError: lib.cloneError,
            actionError: error,
          }}
          showTabs={false}
          renderItem={renderItem}
        />
      </ScrollView>
    </PageSheet>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, radius: r, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    content: { padding: s.s4, gap: s.s3 },
    row: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s3,
      minHeight: 44,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    pressed: { opacity: 0.6 },
    rowDisabled: { opacity: 0.5 },
    rowText: { flex: 1, gap: 2 },
    rowName: { color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    rowMeta: { color: p.ink3, fontSize: t.sizes.sm },
    assignCta: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    assignCtaText: { color: p.brand500, fontSize: t.sizes.sm, fontWeight: "700" },
  });
}
